import { MeetingSource } from "@kalyzee/kast-web-sdk";
import { AudioMixerOptions } from "@kalyzee/kast-web-sdk/web-components";
import objectAssignDeep from "object-assign-deep";
import { MeetingSourceExtended } from "../hooks/meeting";
import { appMessage } from "./message";
import { getSettings, setSettings } from "./storage";
import { SecondaryScreenLayout } from "../interfaces/secondaryScreens";
import { LayoutType } from "../interfaces/ui";

export interface SettingsSecret {
  adminPin?: string;
  localPassword?: string | null;
  remotePassword?: string | null;
}

interface AudioVideo<T> {
  audio?: T,
  video?: T,
}

export interface SettingsDebug {
  displaySessionIds?: boolean;
  displayIncomingStreamStats?: boolean;
  displayOutgoingStreamStats?: boolean;
  audio?: {
    mixer?: AudioMixerOptions & { public?: boolean };
  };
  sdkClient?: {
    public?: boolean;
  };
  meetingContext?: {
    public?: boolean;
  };
  maxBitrate?: {
    pedagogy?: {
      webcam?: AudioVideo<number>;
      screensharing?: AudioVideo<number>;
    },
    student?: {
      webcam?: AudioVideo<number>;
      screensharing?: AudioVideo<number>;
    },
    expert?: {
      camera?: AudioVideo<number>;
      screen?: AudioVideo<number>;
      webcam?: AudioVideo<number>;
    },
  }
}

export interface SettingsMessagingBridgeMaster {
  enableBroadcast?: boolean;
}

export interface SettingsMessagingBridgeSlave {
  enableBroadcast?: boolean;
  enableWebsocket?: boolean;
  websocketEndpoint?: string;
}

export interface Settings {
  secret?: SettingsSecret;
  debug?: SettingsDebug;
  roomId?: string;
  bridge?: {
    master?: SettingsMessagingBridgeMaster;
    slave?: SettingsMessagingBridgeSlave;
  };
  sources?: Partial<MeetingSourceExtended>[];
  videoFeedback?: {
    source: MeetingSource;
  };
  webcamFeedback?: {
    source: Partial<MeetingSourceExtended>;
  };
  emission?: {
    sdpFormatter?: {
      offer?: string,
      answer?: string,
    }
  }
  dev?: boolean;
  logs?: boolean | {
    main?: boolean,
    meetingContext?: boolean,
    ovenMediaSession?: {
      reception?: {
        main?: boolean,
        webrtc?: boolean,
      },
      emission?: {
        main?: boolean,
        webrtc?: boolean,
      },
    }
  };
  soundIndicators?: boolean;
  carousel?: {
    debug?: boolean,
    prelaodDelay?: number,
    interval?: number,
    layouts?: SecondaryScreenLayout[],
    filters?: {
      carouselId: string,
      value: string,
    }[],
  },
  ui?: {
    playersLayout?: LayoutType;
    hideLogoutButton?: boolean;
  },
  features?: {
    survey?: boolean;
    board?: boolean;
    boardMatch?: string;
    audioMatch?: string;
  },
}

let settings: Settings | undefined = undefined;
export const loadSettings = (): Settings => {
  if (settings) return settings;
  let data: object = {};
  try {
    const settingsStr: string | null = getSettings();
    if (settingsStr) data = JSON.parse(settingsStr);
    else setSettings(JSON.stringify({}));
  } catch (err) {
    data = {};
  }
  const result: Settings = objectAssignDeep({}, data);
  settings = result;
  return result;
};

type RecKeys = string | { key: string; keys: RecKeys[] };
const findRecursiveValue = (obj: any, value: any): RecKeys[] => {
  if (!obj) return [];
  if (typeof obj !== "object") return [];
  const keys: RecKeys[] = [];
  Object.keys(obj).forEach((k) => {
    if (obj[k] === value) keys.push(k);
    else if (typeof obj === "object") {
      const childKeys = findRecursiveValue(obj[k], value);
      if (childKeys.length) keys.push({ key: k, keys: childKeys });
    }
  });
  return keys;
};

const setRecursiveValue = (obj: any, keys: RecKeys[], value: any) => {
  if (!obj) return [];
  if (typeof obj !== "object") return [];
  keys.forEach((k) => {
    if (typeof k === "string") {
      obj[k] = value;
    } else {
      setRecursiveValue(obj[k.key], k.keys, value);
    }
  });
  return obj;
};

export const mergeSettings = (settings: Partial<Settings>): Settings => {
  const currSettings = loadSettings();
  const result: Settings = objectAssignDeep(currSettings, settings);
  const recursiveKeys: RecKeys[] = findRecursiveValue(settings, "undefined");
  setRecursiveValue(result, recursiveKeys, undefined);
  return result;
};

// Set value only if not defined yet
export const setSettingsAsDefault = (settings: Partial<Settings>): Settings => {
  const currSettings = loadSettings();
  if (!settings) return currSettings;

  const assign = (target: any, source: any): any => {
    if (target === undefined) {
      // assign value as default
      return source;
    }
    if (source === undefined) return target;
    if (typeof source === "object") {
      Object.keys(source).forEach((k) => {
        target[k] = assign(target[k], source[k]);
      });
    }
    return target;
  };

  const result: Settings = assign(currSettings, settings);
  try {
    setSettings(JSON.stringify(result));
    appMessage.postSettings(result);
  } catch (err) {}
  return result;
};

export const saveSettings = (settings: Partial<Settings>): Settings => {
  try {
    setSettings(JSON.stringify(settings));
    appMessage.postSettings(settings);
  } catch (err) {}
  return settings;
};
